import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { LoadingPlaceholder, useResponsive } from '@garnish/core';

import { SweetpassCheckoutContainer } from '../Container';
import { SweetpassTwoColumnsLayout } from '../TwoColumnsLayout';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mirrors the sweetpass checkout layout while its data is loading.
 */
export const SweetpassCheckoutLoadingPlaceholder = () => {
  const { minWidth, match } = useResponsive();

  const titleHeight = match([32, 48, 56]);

  // ─── Large Breakpoints ───────────────────────────────────────────────

  if (minWidth.isMD) {
    return (
      <SweetpassCheckoutContainer>
        <SweetpassTwoColumnsLayout.Container>
          <SweetpassTwoColumnsLayout.LeftColumn>
            <LoadingPlaceholder
              rows={1}
              rowHeight={TICKET_HEIGHT}
              borderRadius={theme.spacing['10']}
              palette="cream"
              style={styles.loadingPlaceholder}
            />
          </SweetpassTwoColumnsLayout.LeftColumn>

          <SweetpassTwoColumnsLayout.RightColumn>
            <SweetpassCheckoutContentPlaceholder titleHeight={titleHeight} hasTicket={false} />
          </SweetpassTwoColumnsLayout.RightColumn>
        </SweetpassTwoColumnsLayout.Container>
      </SweetpassCheckoutContainer>
    );
  }

  // ─── Small Breakpoints ───────────────────────────────────────────────

  return (
    <SweetpassCheckoutContainer>
      <SweetpassCheckoutContentPlaceholder titleHeight={titleHeight} hasTicket />
    </SweetpassCheckoutContainer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const SweetpassCheckoutContentPlaceholder = (props: ContentPlaceholderProps) => {
  const { titleHeight, hasTicket } = props;

  return (
    <>
      <LoadingPlaceholder
        rows={1}
        rowHeight={titleHeight}
        borderRadius={theme.spacing['2']}
        palette="cream"
        style={styles.titlePlaceholder}
      />

      {hasTicket ? (
        <LoadingPlaceholder
          rows={1}
          rowHeight={TICKET_HEIGHT}
          borderRadius={theme.spacing['10']}
          palette="cream"
          style={styles.loadingPlaceholder}
        />
      ) : null}

      <LoadingPlaceholder
        rows={1}
        rowHeight={INFO_HEIGHT}
        borderRadius={theme.radius.large}
        palette="cream"
        style={styles.loadingPlaceholder}
      />

      <View style={styles.total}>
        <LoadingPlaceholder
          rows={1}
          rowHeight={TOTAL_HEIGHT}
          borderRadius={theme.spacing['2']}
          palette="cream"
          style={styles.totalPlaceholder}
        />

        <LoadingPlaceholder
          rows={1}
          rowHeight={TOTAL_HEIGHT}
          borderRadius={theme.spacing['2']}
          palette="cream"
          style={styles.totalPlaceholder}
        />
      </View>

      <LoadingPlaceholder
        rows={1}
        rowHeight={CTA_HEIGHT}
        borderRadius={CTA_HEIGHT / 2}
        palette="cream"
        style={styles.loadingPlaceholder}
      />
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TICKET_HEIGHT = 352;
const INFO_HEIGHT = 360;
const TOTAL_HEIGHT = 24;
const CTA_HEIGHT = 64;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingPlaceholder: {
    width: '100%',
  },
  titlePlaceholder: {
    width: '70%',
  },
  total: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  totalPlaceholder: {
    width: 80,
  },
});

// ─── Props ───────────────────────────────────────────────────────────────────

type ContentPlaceholderProps = {
  titleHeight: number;
  hasTicket: boolean;
};
